import React, { useEffect, useState, useMemo } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { DespatchesTabs } from "../../../../../components/Booking/util";
import Breadcrumbs from "../../../../../components/Generic/Breadcrump";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "../../../../../components/Generic/UITabMaterial";
import TitlePage from "../../../../../components/TitlePage";
import { useAppState } from "../../../../../context/StateContext";
import useYupValidation, {
  useYupSchemas,
  useHandleValidate,
} from "../../../../../hooks/useYupValidation";
import { DespatchProduct } from "../../../../../models/despatch";
import {
  DespatchesDetailsFields,
  DespatchesProductsFields,
  DespatchesShippingItemsFields,
  DespatchesTruckFields,
  DespatchFields,
  UserTypes,
} from "../../../../../util/enums";
import StatusBadge from "../components/StatusBadge";
import { DespatchProvider, useDespatchContext } from "../context/DespatchContext";
import DespatchesActionsButtons from "./components/DespatchesActionsButtons";
import { DespatchDetailsSkeleton } from "./components/Skeletons/details";
import { DespatchGridSkeleton } from "./components/Skeletons/grid";
import { DespatchesDetails } from "./Details";
import useActionDespatch from "./hooks/useAction";
import DespatchProducts from "./Products";
import DespatchShippingItems from "./ShippingItems";
import useStyles from "./styles";
import DespatchTruckTab from "./Truck";
import { BaseFieldProps, DespatchSectionsProps } from "./types";

interface DespatchMaintenanceProps {}

interface DespatchMaintenanceLocationParams {
  viewOnly?: boolean;
  despatchId?: string;
}

const DespatchMaintenanceScreen: React.FC<DespatchMaintenanceProps> = () => {
  const { state } = useLocation<DespatchMaintenanceLocationParams>();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();
  const [viewOnly, setViewOnly] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [disableSave, setDisableSave] = useState<boolean>(false);
  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(DespatchesTabs.DETAILS);

  const isWarehouseUser = currentUser?.typeUser === UserTypes.WAREHOUSE;
  const requestUuid = useMemo(() => uuid(), []);

  const despatchId = state?.despatchId;
  const { despatch, setDespatch, handleUpdateDespatch, setHasUnsavedChanges, hasUnsavedChanges } =
    useDespatchContext();

  const { getDespatchById, getProductsByDespatch, useSaveDespatch } = useActionDespatch();

  const { data: despatchData, isInitialLoading: loadingDespatch } = getDespatchById(
    Number(despatchId)
  );

  const { data: productsData, isInitialLoading: loadingProducts } = getProductsByDespatch(
    Number(despatchId)
  );

  const {
    mutate: saveDespatch,
    isLoading: loadingSaveDespatch,
    isPaused: pausedSaveDespatch,
  } = useSaveDespatch(() => {
    setHasUnsavedChanges(false);
  });

  const validateAndSaveDespatch = () => {
    if (!handleValidate() || !despatch) {
      setDisableSave(false);

      return;
    }

    if (loadingSaveDespatch || pausedSaveDespatch) return;

    saveDespatch(
      {
        id: despatch?.id,
        despatch,
        uuid: requestUuid,
      },
      {
        onSettled: () => setDisableSave(false),
      }
    );
  };

  const onCancel = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace("/app/despatches");
    }
  };

  useEffect(() => {
    if (despatchData) {
      setDespatch((prev) => ({
        ...prev,
        ...despatchData,
        origins: despatchData?.originAddress ? [despatchData?.originAddress] : [],
        destinations: despatchData?.destinationAddress ? [despatchData?.destinationAddress] : [],
      }));
    }
  }, [despatchData]);

  useEffect(() => {
    if (productsData) {
      setDespatch((prev) => ({
        ...prev,
        products: productsData,
      }));
    }
  }, [productsData]);

  const { fieldHasError, getErrorByField, someFieldHasError } = useYupValidation(errors);
  const { stringRequired } = useYupSchemas(translate);

  useEffect(() => {
    if (state && state.viewOnly) setViewOnly(state.viewOnly);
  }, [state]);

  const validateEmptyArray = (array: any[] | undefined, message: string) => {
    const isEmpty = !array?.length;

    if (isEmpty) {
      NotificationToast.error(translate(message));

      return false;
    }

    return true;
  };
  const schema = yup.object().shape({
    status: stringRequired,
    [DespatchesProductsFields.PRODUCTS]: yup
      .array()
      .test(DespatchesProductsFields.PRODUCTS, (array) =>
        validateEmptyArray(array, "youShouldAddAtLeastOneProduct")
      ),
    [DespatchesDetailsFields.ORIGINS]: yup
      .array()
      .test(DespatchesDetailsFields.ORIGINS, (array) =>
        validateEmptyArray(array, "youShouldAddAtLeastOnePickUpAddress")
      ),
    [DespatchesDetailsFields.DESTINATIONS]: yup
      .array()
      .test(DespatchesDetailsFields.DESTINATIONS, (array) =>
        validateEmptyArray(array, "youShouldAddAtLeastOneDeliveryAddress")
      ),
  });

  const handleValidate = () => {
    setDisableSave(true);
    const validated = useHandleValidate(despatch, schema, setErrors);

    return validated;
  };

  const isUpdatingAction = useMemo(
    () => disableSave || loadingDespatch || loadingProducts,
    [disableSave, loadingDespatch, loadingProducts]
  );

  const generateBaseFieldProps = (name: DespatchFields) => {
    const fieldProps: BaseFieldProps = {
      error: fieldHasError(name),
      errorMessage: getErrorByField(name),
      "data-cy": `input-${name}`,
      dataCy: name,
      label: translate(name),
      placeholder: translate(name),
      "aria-label": name,
      disabled: viewOnly,
      name,
    };

    return fieldProps;
  };

  const baseSectionProps: DespatchSectionsProps = {
    generateBaseFieldProps,
    getErrorByField,
    fieldHasError,
    viewOnly,
    errors,
  };

  const detailsPanel: PanelProps = {
    key: DespatchesTabs.DETAILS,
    menuItem: translate("details"),
    className: classes.uiTab,
    loading: loadingDespatch,
    skeleton: DespatchDetailsSkeleton,
    hasError: someFieldHasError(Object.values(DespatchesDetailsFields)),
    render: () => <DespatchesDetails {...baseSectionProps} />,
  };

  const productsPanel: PanelProps = {
    key: DespatchesTabs.PRODUCTS,
    menuItem: translate("products"),
    className: classes.uiTab,
    loading: loadingDespatch,
    skeleton: DespatchGridSkeleton,
    hasError: someFieldHasError(Object.values(DespatchesProductsFields)),
    render: () => (
      <DespatchProducts
        {...baseSectionProps}
        despatchProducts={despatch?.products || []}
        loading={loadingDespatch}
        updateProducts={(products: DespatchProduct[]) => {
          handleUpdateDespatch("products", products);
        }}
      />
    ),
  };

  const shippingItemsPanel: PanelProps = {
    key: DespatchesTabs.SHIPPING_ITEMS,
    menuItem: translate("shippingItems"),
    className: classes.uiTab,
    loading: loadingDespatch,
    skeleton: DespatchGridSkeleton,
    hasError: someFieldHasError(Object.values(DespatchesShippingItemsFields)),
    render: () => (
      <DespatchShippingItems
        loading={loadingDespatch}
        onUpdateItems={(items) => handleUpdateDespatch("shippingItems", items)}
        shippingItems={despatch?.shippingItems || []}
      />
    ),
  };

  const truck: PanelProps = {
    key: DespatchesTabs.TRUCK,
    menuItem: translate("truck"),
    className: classes.uiTab,
    loading: loadingDespatch,
    skeleton: DespatchGridSkeleton,
    hasError: someFieldHasError(Object.values(DespatchesTruckFields)),
    render: () => (
      <DespatchTruckTab
        loading={loadingDespatch}
        onUpdateItems={(items) => handleUpdateDespatch("fleets", items)}
        truck={despatch?.fleets || []}
      />
    ),
  };

  const leftPanels = [detailsPanel, productsPanel, shippingItemsPanel, truck];

  const getSubtitle = () => {
    if (despatchId) {
      return translate("editDespatch");
    }

    return translate("createDespatch");
  };

  return (
    <div className={classes.container}>
      <TitlePage
        left={
          <Breadcrumbs
            items={[
              {
                title: translate("despatch"),
                link: {
                  url: "/app/despatches",
                },
              },
              {
                title: getSubtitle(),
                color: "#465869",
              },
            ]}
          />
        }
        right={
          <Grid
            container
            item
            xs={12}
            sm={6}
            md={6}
            lg={6}
            className={classes.titleDataContainer}
            spacing={1}
          >
            <Grid item>{despatch?.status && <StatusBadge value={despatch.status} />}</Grid>
            <Grid item>
              <span className={classes.titleData}>
                {despatch?.id ? ` ${translate("despatches")} #${despatch?.id}` : ""}
              </span>
            </Grid>
          </Grid>
        }
      />
      <Prompt when={hasUnsavedChanges} message="" />

      <div className={classes.tabs}>
        <UITabMaterial
          leftPanels={leftPanels}
          showRightPanel={false}
          showActivityButton={false}
          leftDataCy="left-despatch-tabs"
          onChangeLeftPanel={(_event, _panel, key) => key && setLeftPanelSelected(key)}
          tabFooter={
            <DespatchesActionsButtons
              loading={isUpdatingAction}
              leftPanelSelected={leftPanelSelected}
              onCancel={onCancel}
              validateAndSaveDespatch={validateAndSaveDespatch}
              viewOnly={viewOnly}
              isWarehouseUser={isWarehouseUser}
              despatchAlreadySubmitted={false}
              despatchId={despatch?.id}
            />
          }
        />
      </div>
    </div>
  );
};

const DespatchMaintenance = () => {
  return (
    <DespatchProvider>
      <DespatchMaintenanceScreen />
    </DespatchProvider>
  );
};

export default DespatchMaintenance;
